import React, { useState } from "react"
import { Grid, ButtonBase } from "@material-ui/core"
import clsx from "clsx"

import { ActionDialog, Notification } from "@/components"
import ExternalPlatformInput from "@/components/ExternalPlatformInput"

import { integrationInfo } from "@/utils/integration"

import { IntegrationPlatform, IntegrationType } from "@/protocols/integration"
import { WebhookTemplateType } from "@/protocols/webhook"

import ApiService from "@/services/Api"
import ErrorHandler from "@/services/ErrorHandler"
import HardCoded from "@/services/HardCoded"

import PurposeIntegrationDialog from "@/pages/Admin/Integration/PurposeIntegrationDialog"

import useStyles from "@/pages/Admin/Integration/NewIntegrationDialog/styles"

import { getErrorCodeMessages } from "@/utils/response"

import { ErrorType } from "@/hooks/useValidation"

export type NewIntegrationDialogProps = {
	integrationPlatforms: IntegrationPlatform[]
	onCreate: (integrationId: number) => void
	onClose: () => void
	opened: boolean
}

type PlatformInIntegrationAndChatBot = Extract<IntegrationType, "ActiveCampaign" | "RDStation" | "DirectIntegration">

const NewIntegrationDialog: React.FC<NewIntegrationDialogProps> = (props) => {
	const {
		integrationPlatforms,
		onCreate,
		onClose,
		opened
	} = props

	const [selectedIntegrationPlatform, setSelectedIntegrationPlatform] = useState<IntegrationPlatform>(integrationPlatforms[0])
	const [externalPlatformName, setExternalPlatformName] = useState<string>("")
	const [loadingCreation, setLoadingCreation] = useState(false)

	const isAbleCreateChatBotByIntegration = HardCoded.checkFeatureFlag("createChatBotByIntegration")
	const isDirectIntegration = selectedIntegrationPlatform?.type === "DirectIntegration"

	const classes = useStyles()

	const handleCreateIntegration = async (externalPlatformName?: string) => {
		setLoadingCreation(true)

		const normalizedPlatformName = externalPlatformName?.trim().length
			? externalPlatformName.trim()
			: undefined

		const payload = {
			integrationPlatformId: selectedIntegrationPlatform.id,
			title: selectedIntegrationPlatform.title,
			isActive: true,
			externalPlatformName: normalizedPlatformName
		}

		try {
			const response = await ApiService.post("/integrations", payload)
			const integrationId = response.data.integration.id
			onCreate(integrationId)

			Notification.success({ message: "Integração criada com sucesso!" })
		} catch (error) {
			ErrorHandler.handle(error as ErrorType)

			const codeMessages = getErrorCodeMessages(error as ErrorType)

			if (codeMessages?.inboxChannelId === "integration_not_supported_for_waba_channel") {
				Notification.warning({ message: "Não é possível criar integrações na API WABA" })
			} else {
				Notification.error({ message: "Não foi possível criar a integração" })
			}
		}

		setLoadingCreation(false)
	}

	const handleSelectIntegrationPlatform = (integrationPlatformId: number) => {
		const integrationPlatform = integrationPlatforms.find(({ id }) => id === integrationPlatformId)

		if (integrationPlatform) {
			setSelectedIntegrationPlatform(integrationPlatform)
			setExternalPlatformName("")
		}
	}

	const handleSave = async () => {
		if (!isAbleCreateChatBotByIntegration) {
			await handleCreateIntegration(externalPlatformName)
			return
		}

		const platformInIntegrationAndChatBot: Record<PlatformInIntegrationAndChatBot, WebhookTemplateType | "custom"> = {
			DirectIntegration: "custom",
			RDStation: "rd-station",
			ActiveCampaign: "active-campaign"
		}

		if (platformInIntegrationAndChatBot[selectedIntegrationPlatform.type as PlatformInIntegrationAndChatBot]) {
			onClose()

			PurposeIntegrationDialog.open({
				createIntegration: handleCreateIntegration,
				webhookTemplate: platformInIntegrationAndChatBot[selectedIntegrationPlatform.type as PlatformInIntegrationAndChatBot],
				externalPlatformName
			})
		} else {
			await handleCreateIntegration(externalPlatformName)
		}
	}

	return (
		<ActionDialog
			saveButtonId="add-new-integration-save-button"
			title="QUAL PLATAFORMA DESEJA INTEGRAR?"
			onClose={onClose}
			openDialog={opened}
			fullWidth
			saveText="CRIAR INTEGRAÇÃO"
			onSave={handleSave}
			loading={loadingCreation}
		>
			<Grid container justifyContent="flex-start" alignItems="center" alignContent="center" spacing={2}>
				{integrationPlatforms.map(integrationPlatform => {
					const isHotmartPlatform = integrationPlatform.id === 4

					if (isHotmartPlatform) {
						return
					}

					return (
						<Grid
							container
							xs={4}
							alignItems="center"
							justify="center"
							alignContent="center"
							key={integrationPlatform.id}
						>
							<Grid
								id={`listed-integration-${integrationPlatform.id}`}
								component={ButtonBase}
								onClick={() => handleSelectIntegrationPlatform(integrationPlatform.id)}
								item
								className={clsx({
									[classes.integrationPlatformContainer]: true,
									[classes.selectedIntegrationPlatform]: integrationPlatform.id === selectedIntegrationPlatform.id,
									[classes.nonSelectedIntegrationPlatform]: integrationPlatform.id !== selectedIntegrationPlatform.id
								})}
								xs={11}
							>
								<img
									src={integrationInfo[integrationPlatform.type]?.logo}
									alt={integrationPlatform.title}
									className={classes.integrationPlatformImg}
								/>
							</Grid>
						</Grid>
					)
				})}
			</Grid>

			<ExternalPlatformInput
				open={isDirectIntegration}
				value={externalPlatformName}
				onChange={setExternalPlatformName}
			/>
		</ActionDialog>
	)
}

export default NewIntegrationDialog
